import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, ChevronDown, ChevronRight, CheckCircle, XCircle } from 'lucide-react';
import Spinner from '../components/ui/Spinner';
import Badge from '../components/ui/Badge';
import Pagination from '../components/ui/Pagination';
import { getUsers, getUser, type UserDetail } from '../services/userService';

export default function UsersPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [emailVerified, setEmailVerified] = useState('');

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedDetail, setExpandedDetail] = useState<UserDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users', { page, search, emailVerified }],
    queryFn: () => getUsers({ page, limit: 20, search: search || undefined, emailVerified: emailVerified || undefined }),
  });

  const toggleExpand = async (userId: string) => {
    if (expandedId === userId) {
      setExpandedId(null);
      setExpandedDetail(null);
      return;
    }
    setExpandedId(userId);
    setExpandedDetail(null);
    setDetailLoading(true);
    try {
      const detail = await getUser(userId);
      setExpandedDetail(detail);
    } catch {
      setExpandedDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-bold text-gray-900">사용자 현황</h1>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="이름 / 이메일 / 조직명 검색"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-primary-500" />
        </div>
        <select value={emailVerified} onChange={(e) => { setEmailVerified(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm outline-none focus:ring-1 focus:ring-primary-500">
          <option value="">전체 인증</option><option value="true">인증 완료</option><option value="false">미인증</option>
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner className="size-8" /></div>
      ) : (
        <>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="w-8 px-3 py-3" />
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">이름</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">이메일</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">가입일</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">인증</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-500 uppercase">소속 조직</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data?.items.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-400">사용자가 없습니다.</td></tr>
                ) : data?.items.map((user) => {
                  const isExpanded = expandedId === user.id;
                  const isUnverified = !user.emailVerifiedAt;
                  const noOrg = user.organizationCount === 0;
                  const rowBg = isUnverified ? 'bg-yellow-50' : noOrg ? 'bg-red-50/50' : '';

                  return (
                    <Fragment key={user.id}>
                      <tr onClick={() => toggleExpand(user.id)}
                        className={`hover:bg-gray-50 cursor-pointer ${rowBg}`}>
                        <td className="px-3 py-3 text-gray-400">
                          {isExpanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{user.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{user.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{new Date(user.createdAt).toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' })}</td>
                        <td className="px-4 py-3">
                          {user.emailVerifiedAt
                            ? <CheckCircle className="size-4 text-green-500" />
                            : <XCircle className="size-4 text-red-400" />}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {user.organizationCount === 0 ? (
                            <span className="text-red-500">없음</span>
                          ) : (
                            <span className="text-gray-600">
                              {user.organizations.map((o) => o.name).join(', ')}
                              {user.organizationCount > 2 && ` (${user.organizationCount})`}
                            </span>
                          )}
                        </td>
                      </tr>

                      {/* Expanded detail */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={6} className="bg-gray-50/80 px-8 py-4">
                            {detailLoading ? (
                              <div className="flex justify-center py-4"><Spinner className="size-5" /></div>
                            ) : expandedDetail ? (
                              <div className="grid grid-cols-2 gap-6">
                                {/* 소속 조직 */}
                                <div>
                                  <h4 className="text-sm font-semibold text-gray-500 uppercase mb-2">소속 조직</h4>
                                  {expandedDetail.organizations.length === 0 ? (
                                    <p className="text-sm text-gray-400">소속 조직이 없습니다.</p>
                                  ) : (
                                    <div className="space-y-1.5">
                                      {expandedDetail.organizations.map((o) => (
                                        <div key={o.id} className="flex items-center gap-2">
                                          <button onClick={(e) => { e.stopPropagation(); navigate(`/organizations/${o.id}`); }}
                                            className="text-sm text-primary-600 hover:underline">{o.name}</button>
                                          <Badge variant={o.role === 'OWNER' ? 'warning' : o.role === 'ADMIN' ? 'info' : 'default'}>{o.role}</Badge>
                                          <span className="text-sm text-gray-400">{new Date(o.joinedAt).toLocaleDateString('ko-KR')}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                {/* 프로젝트 참여 */}
                                <div>
                                  <h4 className="text-sm font-semibold text-gray-500 uppercase mb-2">프로젝트 참여</h4>
                                  {expandedDetail.projects.length === 0 ? (
                                    <p className="text-sm text-gray-400">참여 프로젝트가 없습니다.</p>
                                  ) : (
                                    <div className="space-y-1.5">
                                      {expandedDetail.projects.map((p) => (
                                        <div key={p.id} className="flex items-center gap-2">
                                          <span className="text-sm text-gray-900">{p.name}</span>
                                          <span className="text-sm bg-gray-100 text-gray-500 rounded px-1.5 py-0.5">{p.projectKey}</span>
                                          <Badge variant="default">{p.role}</Badge>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-gray-400 text-center">상세 정보를 불러오지 못했습니다.</p>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {data && <Pagination page={data.page} totalPages={data.totalPages} onPageChange={setPage} />}
        </>
      )}
    </div>
  );
}

// Fragment import needed
import { Fragment } from 'react';
